import React, { useRef, useEffect, useCallback, useMemo, useId } from 'react';
import { Icon, WarningIcon, AtIcon } from '@phosphor-icons/react';
import { MentionData } from 'src/hooks/useMentionUsers';
import { useMentions } from 'src/hooks/useMentions';

import { countCodepoints } from './characterCount';

function renderMentionOverlay(text: string, mentions: MentionData[]): React.ReactNode {
  const displayTexts = mentions.map((m) => m.displayText).filter((dt): dt is string => !!dt);
  if (!displayTexts.length || !text) {
    return (
      <>
        {text}
        {text.endsWith('\n') && ' '}
      </>
    );
  }

  const escaped = displayTexts.sort((a, b) => b.length - a.length).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'g');
  const mentionSet = new Set(displayTexts);
  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, i) =>
        mentionSet.has(part) ? (
          <strong key={i} className="font-bold text-content-default">
            {part}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
      {text.endsWith('\n') && ' '}
    </>
  );
}

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  icon?: Icon;
  onIconClick?: () => void;
  fullHeight?: boolean;
  fullWidth?: boolean;
  wrapperClassName?: string;
  state?: 'default' | 'error';
  errorMessage?: string;
  /** When provided alongside onMentionsChange, enables @-mention support with a dropdown. */
  mentions?: MentionData[];
  onMentionsChange?: (mentions: MentionData[]) => void;
  /** When provided with mentions, includes group members (potentially cross-tenant) in mention search. */
  mentionGroupId?: string | null;
  /** When set, renders a codepoint-based ``count/limit`` counter beneath the input and applies error styling once exceeded. */
  characterLimit?: number;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className = '',
      rows = 3,
      icon,
      onIconClick,
      fullHeight = false,
      fullWidth = false,
      wrapperClassName,
      state = 'default',
      errorMessage,
      mentions,
      onMentionsChange,
      mentionGroupId,
      characterLimit,
      ...props
    },
    ref
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const visualState = state === 'error' ? 'error' : 'default';
    const mentionsEnabled = mentions !== undefined && onMentionsChange !== undefined;
    const generatedCounterId = useId();
    const counterId = characterLimit !== undefined ? generatedCounterId : undefined;
    const valueAsString = typeof props.value === 'string' ? props.value : '';
    const codepointCount = useMemo(
      () => (characterLimit === undefined ? 0 : countCodepoints(valueAsString)),
      [characterLimit, valueAsString]
    );
    const overLimit = characterLimit !== undefined && codepointCount > characterLimit;
    const describedBy = [props['aria-describedby'], counterId].filter(Boolean).join(' ') || undefined;

    useEffect(() => {
      if (ref && typeof ref !== 'function') {
        ref.current = innerRef.current;
      }
    }, [ref]);

    const autoResize = useCallback((textarea: HTMLTextAreaElement) => {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }, []);

    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      autoResize(e.target);
      if (props.onInput) props.onInput(e);
    };

    useEffect(() => {
      if (innerRef.current) {
        autoResize(innerRef.current);
      }
    }, [props.value, autoResize]);

    const borderClass =
      visualState === 'error' ? 'border-misc-error' : 'border-border-default focus-within:border-border-active';

    const iconToRender = mentionsEnabled ? AtIcon : icon;
    const iconClickHandler = mentionsEnabled ? undefined : onIconClick;

    const setRef = useCallback(
      (el: HTMLTextAreaElement | null) => {
        innerRef.current = el;
        if (typeof ref === 'function') ref(el);
      },
      [ref]
    );

    return (
      <div
        className={`flex flex-col gap-[4px] ${fullHeight ? 'h-full' : ''} ${fullWidth ? 'w-full' : ''} ${mentionsEnabled ? 'relative' : ''}`}
      >
        <div
          className={`flex items-start border-[2px] bg-white w-full ${fullHeight ? 'h-full' : ''} px-[13px] py-[12px] gap-2 rounded-[6px] text-[14px] font-medium font-raleway leading-[100%] ${borderClass} text-content-subtle focus-within:text-content-default ${props.disabled ? 'opacity-60 cursor-not-allowed bg-bluegray-50' : ''} ${wrapperClassName || ''}`}
        >
          {mentionsEnabled ? (
            <MentionTextareaInner
              setRef={setRef}
              innerRef={innerRef}
              className={className}
              rows={rows}
              mentions={mentions}
              onMentionsChange={onMentionsChange}
              mentionGroupId={mentionGroupId}
              autoResize={autoResize}
              {...props}
              aria-describedby={describedBy}
            />
          ) : (
            <textarea
              ref={setRef}
              className={`w-full outline-none bg-transparent resize-none max-h-[320px] overflow-y-auto leading-[1.5] ${className}`}
              rows={rows}
              {...props}
              aria-describedby={describedBy}
              onInput={handleInput}
            />
          )}
          {iconToRender && (
            <div
              className={`text-content-subtlest ${iconClickHandler ? 'cursor-pointer' : ''} mt-1`}
              onClick={iconClickHandler}
              title={mentionsEnabled ? 'Type @ to mention someone' : undefined}
            >
              {React.createElement(iconToRender, { size: mentionsEnabled ? 18 : 20, weight: 'regular' })}
            </div>
          )}
        </div>
        {(visualState === 'error' && errorMessage) || characterLimit !== undefined ? (
          <div className="flex items-center justify-between gap-3 mt-[3px]">
            {visualState === 'error' && errorMessage ? (
              <div className="flex items-center gap-[10px] text-[#FF0000] font-raleway font-normal text-[14px] leading-[120%] tracking-[0] align-middle">
                <span>
                  <WarningIcon size={20} weight="regular" color="#FF0000" />
                </span>
                <span>{errorMessage}</span>
              </div>
            ) : (
              <span />
            )}
            {characterLimit !== undefined && (
              <span
                id={counterId}
                aria-live="polite"
                className={`text-xs font-raleway lining-nums tabular-nums ml-auto ${
                  overLimit
                    ? 'rounded-md bg-red-100 px-1.5 py-0.5 font-semibold text-content-danger'
                    : 'text-content-subtle'
                }`}
              >
                {codepointCount}/{characterLimit}
              </span>
            )}
          </div>
        ) : null}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

interface MentionTextareaInnerProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'ref'> {
  setRef: (el: HTMLTextAreaElement | null) => void;
  innerRef: React.RefObject<HTMLTextAreaElement | null>;
  className: string;
  rows: number;
  mentions: MentionData[];
  onMentionsChange: (mentions: MentionData[]) => void;
  mentionGroupId?: string | null;
  autoResize: (textarea: HTMLTextAreaElement) => void;
}

function MentionTextareaInner({
  setRef,
  innerRef,
  className,
  rows,
  mentions,
  onMentionsChange,
  mentionGroupId,
  autoResize,
  value,
  onChange,
  onKeyDown,
  ...props
}: MentionTextareaInnerProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const textValue = typeof value === 'string' ? value : '';
  const hasMentions = mentions.some((m) => m.displayText);

  const onValueChange = useCallback(
    (newValue: string) => {
      if (onChange) {
        const syntheticEvent = {
          target: { value: newValue }
        } as React.ChangeEvent<HTMLTextAreaElement>;
        onChange(syntheticEvent);
      }
    },
    [onChange]
  );

  const mention = useMentions({
    value: textValue,
    onChange: onValueChange,
    mentions,
    onMentionsChange,
    textareaRef: innerRef,
    groupId: mentionGroupId
  });

  const combinedOnChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      autoResize(e.target);
      mention.handleChange(e);
    },
    [autoResize, mention.handleChange]
  );

  const combinedOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      mention.handleKeyDown(e);
      if (!e.defaultPrevented && onKeyDown) {
        onKeyDown(e);
      }
    },
    [mention.handleKeyDown, onKeyDown]
  );

  const handleScroll = useCallback(() => {
    if (overlayRef.current && innerRef.current) {
      overlayRef.current.scrollTop = innerRef.current.scrollTop;
    }
  }, [innerRef]);

  const overlayContent = useMemo(() => renderMentionOverlay(textValue, mentions), [textValue, mentions]);

  return (
    <>
      <div className="relative w-full">
        {hasMentions && (
          <div
            ref={overlayRef}
            className={`absolute inset-0 pointer-events-none whitespace-pre-wrap break-words overflow-hidden max-h-[320px] leading-[1.5] ${className}`}
            aria-hidden="true"
          >
            {overlayContent}
          </div>
        )}
        <textarea
          ref={setRef}
          className={`w-full outline-none bg-transparent resize-none max-h-[320px] overflow-y-auto leading-[1.5] ${hasMentions ? 'text-transparent caret-gray-900 selection:bg-blue-200' : ''} ${className}`}
          rows={rows}
          value={value}
          {...props}
          onChange={combinedOnChange}
          onKeyDown={combinedOnKeyDown}
          onScroll={handleScroll}
        />
      </div>
      {mention.showDropdown && mention.hasResults && (
        <MentionDropdown
          dropdownRef={mention.dropdownRef}
          suggestions={mention.suggestions}
          loading={mention.loading}
          emailInput={mention.emailInput}
          selectedIndex={mention.selectedIndex}
          setSelectedIndex={mention.setSelectedIndex}
          insertMention={mention.insertMention}
          insertEmailMention={mention.insertEmailMention}
        />
      )}
    </>
  );
}

interface MentionDropdownProps {
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  suggestions: Array<{ user_id: number; email: string; firstname: string | null; surname: string | null }>;
  loading: boolean;
  emailInput: string;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  insertMention: (user: { user_id: number; email: string; firstname: string | null; surname: string | null }) => void;
  insertEmailMention: (email: string) => void;
}

function MentionDropdown({
  dropdownRef,
  suggestions,
  loading,
  emailInput,
  selectedIndex,
  setSelectedIndex,
  insertMention,
  insertEmailMention
}: MentionDropdownProps) {
  return (
    <div
      ref={dropdownRef}
      className="absolute left-0 right-0 z-50 mt-1 top-full max-h-[200px] overflow-y-auto bg-white border border-border-default rounded-lg shadow-lg"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {suggestions.map((user, index) => (
        <button
          key={user.user_id}
          type="button"
          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-surface-hover ${
            index === selectedIndex ? 'bg-surface-hover' : ''
          }`}
          onMouseDown={(e) => {
            e.preventDefault();
            insertMention(user);
          }}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="w-7 h-7 rounded-full bg-secondary-blue text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
            {(user.firstname?.[0] || user.email[0]).toUpperCase()}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-content-default truncate">
              {user.firstname} {user.surname}
            </span>
            <span className="text-xs text-content-subtlest truncate">{user.email}</span>
          </div>
        </button>
      ))}

      {emailInput && (
        <button
          type="button"
          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-surface-hover border-t border-border-subtle ${
            selectedIndex === suggestions.length ? 'bg-surface-hover' : ''
          }`}
          onMouseDown={(e) => {
            e.preventDefault();
            insertEmailMention(emailInput);
          }}
          onMouseEnter={() => setSelectedIndex(suggestions.length)}
        >
          <div className="w-7 h-7 rounded-full bg-grey-400 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
            <AtIcon size={14} />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-content-default truncate">Invite {emailInput}</span>
            <span className="text-xs text-content-subtlest truncate">Send notification to this email</span>
          </div>
        </button>
      )}

      {loading && suggestions.length === 0 && !emailInput && (
        <div className="px-3 py-2 text-sm text-content-subtlest">Searching...</div>
      )}
    </div>
  );
}

export { Textarea };
export default Textarea;
